import { apiService } from './api';
import { AppNotification, NotificationListResponse, TaskSnooze } from '../types';

// Re-export for consumers that import from services/notifications
export type { AppNotification, NotificationListResponse, TaskSnooze };

export interface NotificationListFilters {
  unread_only?: boolean;
  kind?: string;
  include_dismissed?: boolean;
  limit?: number;
  offset?: number;
}

export const NOTIFICATIONS_CHANGED = 'h2okay:notifications-changed';

/** Notify both app shells after a successful mutation. */
async function changed<T>(request: Promise<T>): Promise<T> {
  const result = await request;
  window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED));
  return result;
}

export const notificationsService = {
  async list(filters?: NotificationListFilters): Promise<NotificationListResponse> {
    const params: Record<string, string> = {};
    if (filters?.unread_only) params.unread_only = 'true';
    if (filters?.kind) params.kind = filters.kind;
    if (filters?.include_dismissed) params.include_dismissed = 'true';
    if (filters?.limit !== undefined) params.limit = String(filters.limit);
    if (filters?.offset !== undefined) params.offset = String(filters.offset);
    return apiService.get<NotificationListResponse>('/notifications/', params);
  },

  async unreadCount(): Promise<{ count: number }> {
    return apiService.get<{ count: number }>('/notifications/unread-count');
  },

  async markRead(ids: string[]): Promise<{ updated: number }> {
    return changed(apiService.post<{ updated: number }>('/notifications/mark-read', { ids }));
  },

  async markAllRead(): Promise<{ updated: number }> {
    return changed(apiService.post<{ updated: number }>('/notifications/mark-read', {
      all: true,
    }));
  },

  async dismiss(ids: string[]): Promise<{ updated: number }> {
    return changed(apiService.post<{ updated: number }>('/notifications/dismiss', { ids }));
  },

  async dismissAll(): Promise<{ updated: number }> {
    return changed(apiService.post<{ updated: number }>('/notifications/dismiss', {
      all: true,
    }));
  },

  /** Undo for dismiss — clears dismissed_at on the given rows. */
  async restore(ids: string[]): Promise<{ updated: number }> {
    return changed(apiService.post<{ updated: number }>('/notifications/dismiss', {
      ids,
      restore: true,
    }));
  },

  async snooze(taskIds: string[], days: 7 | 30 | 90): Promise<{ snoozes: TaskSnooze[] }> {
    return changed(apiService.post<{ snoozes: TaskSnooze[] }>('/notifications/snooze', {
      task_ids: taskIds,
      days,
    }));
  },

  async unsnooze(taskId: string): Promise<void> {
    await changed(apiService.delete<void>(`/notifications/snooze/${taskId}`));
  },

  async listSnoozes(): Promise<TaskSnooze[]> {
    return apiService.get<TaskSnooze[]>('/notifications/snoozes');
  },
};
